/**
 * Types, English fallbacks, and facet-value mapping for the localized
 * search labels coming from the Contentful `searchLabels` content type
 * (see SearchLabelsFragment).
 */

export interface SearchLabels {
  search: string
  recentSearches: string
  commonSearches: string
  suggestions: string
  searchIn: string
  reset: string
  page: string
  of: string
  results: string
  noResults: string
  category: string
  article: string
  video: string
  filteredBy: string
  type: string
  industry: string
  language: string
  resetSearch: string
  removeFilter: string
  previous: string
  next: string
  productInfo: string
  support: string
  newsAndEvents: string
  document: string
  caseStudy: string
  customerStory: string
  landingPage: string
  faq: string
}

/** English defaults matching the migration default values. */
export const DEFAULT_SEARCH_LABELS: SearchLabels = {
  search: "Search",
  recentSearches: "Recent Searches",
  commonSearches: "Common Searches",
  suggestions: "Suggestions",
  searchIn: "Search in",
  reset: "Reset",
  page: "Page",
  of: "of",
  results: "results",
  noResults: "No results",
  category: "Category",
  article: "Article",
  video: "Video",
  filteredBy: "Filtered by",
  type: "Type",
  industry: "Industry",
  language: "Language",
  resetSearch: "Reset search",
  removeFilter: "Remove filter",
  previous: "Previous",
  next: "Next",
  productInfo: "Product Info",
  support: "Support",
  newsAndEvents: "News and Events",
  document: "Document",
  caseStudy: "Case Study",
  customerStory: "Customer Story",
  landingPage: "Landing Page",
  faq: "FAQ",
}

/** Maps a normalized Algolia contentType facet value to a SearchLabels key. */
const CONTENT_TYPE_LABEL_KEYS: Record<string, keyof SearchLabels> = {
  page: "page",
  // Tag pages share the single "page" label with regular pages.
  tagpage: "page",
  article: "article",
  video: "video",
  document: "document",
  casestudy: "caseStudy",
  customerstory: "customerStory",
  landingpage: "landingPage",
  faq: "faq",
  productinfo: "productInfo",
  support: "support",
  newsandevents: "newsAndEvents",
}

/**
 * Translates an Algolia `contentType` facet value (e.g. "Case Study") using
 * the localized labels. Falls back to the raw facet value when there is no
 * mapping (index values may already be localized).
 */
export function getContentTypeLabel(
  facetValue: string,
  labels: SearchLabels,
): string {
  if (!facetValue) return facetValue
  const normalized = facetValue.toLowerCase().replace(/[^a-z0-9]/g, "")
  const key = CONTENT_TYPE_LABEL_KEYS[normalized]
  return key ? labels[key] : facetValue
}

/** Merges partially-populated labels from Contentful over the English defaults. */
export function mergeSearchLabels(
  labels?: Partial<SearchLabels> | null,
): SearchLabels {
  if (!labels) return DEFAULT_SEARCH_LABELS
  const merged: SearchLabels = { ...DEFAULT_SEARCH_LABELS }
  for (const key of Object.keys(merged) as (keyof SearchLabels)[]) {
    const value = labels[key]
    if (typeof value === "string" && value.length > 0) {
      merged[key] = value
    }
  }
  return merged
}
